/** Home: My Tasks with Today / Overdue / Next / Later / Unscheduled groups and a Done tab. */
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, BellRing, Check, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import {
  MY_TASKS_GROUP_LABEL,
  MY_TASKS_GROUP_ORDER,
  STATUS_GROUP_ORDER,
  defaultDueDateForGroup,
  doneTasks,
  formatFireAt,
  greeting,
  groupMyTasks,
  toLocalParts,
  type GroupSort,
  type MyTasksGroupKey,
  type Task,
  type TaskSortKey,
} from '@karya/core';
import { cn } from '@/lib/cn';
import { useIsDesktop, useNow } from '@/lib/hooks';
import { useAllTasks, useProfile, useTimeSettings, useUpcomingReminders } from '@/data/queries';
import * as m from '@/data/mutations';
import { usePrefs, useUI } from '@/state/ui';
import { useOpenTask } from '@/routes/nav';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import { IconButton } from '@/components/ui/button';
import { TaskRow } from '@/components/task/task-row';
import { InlineAdd } from '@/components/task/inline-add';
import { StatusIcon } from '@/components/task/status';
import { useTaskRowData } from '@/components/task/use-task-row-data';
import { PageHeader } from '@/components/shell/PageHeader';
import { PullIndicator, usePullToRefresh } from '@/lib/pull-to-refresh';
import { GridRow } from '@/components/ui/grid';

const SORT_LABEL: Record<TaskSortKey, string> = {
  status: 'Status',
  due_date: 'Due date',
  created: 'Date created',
  updated: 'Date updated',
  priority: 'Priority',
  manual: 'Custom',
};

type Item =
  | { kind: 'header'; group: MyTasksGroupKey; count: number; key: string }
  | { kind: 'task'; task: Task; key: string; group: MyTasksGroupKey }
  | { kind: 'add'; group: MyTasksGroupKey; key: string }
  | { kind: 'empty'; key: string; text: string };

export function MyTasksView() {
  const desktop = useIsDesktop();
  const now = useNow();
  const profile = useProfile();
  const { tz, nextDays } = useTimeSettings();
  const tasks = useAllTasks();
  const { ws, resolve } = useTaskRowData();
  const openTask = useOpenTask();
  const [tab, setTab] = useState<'todo' | 'done'>('todo');
  const collapsed = usePrefs((s) => s.myTasksCollapsed);
  const toggle = usePrefs((s) => s.toggleMyTasksGroup);
  const sorts = usePrefs((s) => s.myTasksSort);
  const setSort = usePrefs((s) => s.setMyTasksSort);
  const openQuickAdd = useUI((s) => s.openQuickAdd);
  const focusedId = useUI((s) => s.focusedTaskId);
  const setFocused = useUI((s) => s.setFocusedTaskId);

  const enriched = useMemo(
    () =>
      (tasks ?? []).map((t) => ({
        ...t,
        status_type: ws.statusById.get(t.status_id)?.type ?? 'not_started',
      })),
    [tasks, ws],
  );
  const statusRank = useMemo(() => {
    const rank = new Map<string, number>();
    for (const s of ws.statuses)
      rank.set(s.id, STATUS_GROUP_ORDER.indexOf(s.type) * 100000 + s.position / 1e6);
    return (t: { status_id?: string }) => rank.get(t.status_id ?? '') ?? 0;
  }, [ws.statuses]);
  const groups = useMemo(
    () => groupMyTasks(enriched, { now, tz, nextDays, statusRank: statusRank as never }, sorts),
    [enriched, now, tz, nextDays, sorts, statusRank],
  );
  const done = useMemo(() => doneTasks(enriched), [enriched]);
  const quickAddListId = profile.default_quick_add_list_id ?? ws.lists[0]?.id ?? null;

  const items: Item[] = useMemo(() => {
    if (tab === 'done') {
      return done.length
        ? done.map((t) => ({ kind: 'task' as const, task: t, key: t.id, group: 'today' as const }))
        : [{ kind: 'empty', key: 'empty', text: 'Nothing completed yet.' }];
    }
    const out: Item[] = [];
    for (const g of MY_TASKS_GROUP_ORDER) {
      const list = groups[g];
      if (g === 'later' && list.length === 0) continue;
      out.push({ kind: 'header', group: g, count: list.length, key: `h-${g}` });
      if (collapsed[g]) continue;
      for (const t of list) out.push({ kind: 'task', task: t, key: t.id, group: g });
      if (g === 'today' || g === 'unscheduled' || g === 'next')
        out.push({ kind: 'add', group: g, key: `a-${g}` });
    }
    return out;
  }, [tab, done, groups, collapsed]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const onRowOpen = useCallback((t: Task) => openTask(t.id), [openTask]);
  const onRowFocus = useCallback((t: Task) => setFocused(t.id), [setFocused]);
  const { pull, busy } = usePullToRefresh(scrollRef);
  const rowH = desktop ? 32 : 53;
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) =>
      items[i]?.kind === 'header'
        ? desktop
          ? 32
          : 40
        : items[i]?.kind === 'add'
          ? desktop
            ? 32
            : 44
          : rowH,
    overscan: 12,
    getItemKey: (i) => items[i]!.key,
  });

  const localHour = toLocalParts(now, tz).hour;
  const firstName = (profile.display_name ?? 'there').split(' ')[0];
  const dueToday = groups.today.length;
  const overdue = groups.overdue.length;

  const renderItem = (it: Item): ReactNode => {
    if (it.kind === 'empty')
      return <div className="px-4 py-8 text-center text-sm text-ink-3">{it.text}</div>;
    if (it.kind === 'header') {
      const sort = sorts[it.group];
      return (
        <GroupHeader
          label={MY_TASKS_GROUP_LABEL[it.group]}
          count={it.count}
          tone={it.group === 'overdue' && it.count > 0 ? 'overdue' : undefined}
          collapsed={!!collapsed[it.group]}
          onToggle={() => toggle(it.group)}
          phone={!desktop}
          sort={sort}
          onSort={(s) => setSort(it.group, s)}
          onAddTask={() =>
            openQuickAdd({
              tab: 'task',
              listId: quickAddListId,
              dueDate: defaultDueDateForGroup(it.group, { now, tz }),
            })
          }
          onAddReminder={() =>
            openQuickAdd({
              tab: 'reminder',
              dueDate: defaultDueDateForGroup(it.group, { now, tz }),
            })
          }
        />
      );
    }
    if (it.kind === 'add') {
      return (
        <InlineAdd
          listId={quickAddListId}
          dueDate={defaultDueDateForGroup(it.group, { now, tz })}
          phone={!desktop}
        />
      );
    }
    const r = resolve(it.task);
    const parent =
      tab === 'done' && it.task.parent_task_id
        ? tasks?.find((t) => t.id === it.task.parent_task_id)
        : undefined;
    return (
      <div>
        {parent && (
          <GridRow
            className="truncate px-3 pt-1 text-[11px] leading-3 text-ink-3"
            style={{ paddingLeft: desktop ? 36 : 52 }}
          >
            {parent.name}
          </GridRow>
        )}
        <TaskRow
          task={it.task}
          status={r.status}
          statuses={r.statuses}
          tags={r.tags}
          list={r.list}
          space={r.space}
          now={now}
          tz={tz}
          variant="mytasks"
          phone={!desktop}
          pending={r.pending}
          focused={focusedId === it.task.id}
          onFocus={onRowFocus}
          onOpen={onRowOpen}
        />
      </div>
    );
  };

  const list = (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto scroll-thin">
      <PullIndicator pull={pull} busy={busy} />
      <div
        role="grid"
        aria-label={tab === 'todo' ? 'My tasks to do' : 'Completed tasks'}
        style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
      >
        {virtualizer.getVirtualItems().map((v) => (
          <div
            key={v.key}
            data-index={v.index}
            ref={virtualizer.measureElement}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              transform: `translateY(${v.start}px)`,
            }}
          >
            {items[v.index]!.kind === 'task' ? (
              renderItem(items[v.index]!)
            ) : (
              <GridRow>{renderItem(items[v.index]!)}</GridRow>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const tabs = (
    <div
      className={cn('flex items-center gap-1', desktop ? 'border-b border-line px-4' : 'px-4 pb-2')}
      role="tablist"
      aria-label="My tasks"
    >
      {(['todo', 'done'] as const).map((t) =>
        desktop ? (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cn(
              '-mb-px h-10 border-b-2 px-2 font-medium',
              tab === t
                ? 'border-primary text-ink'
                : 'border-transparent text-ink-2 hover:text-ink',
            )}
          >
            {t === 'todo' ? 'To Do' : 'Done'}
          </button>
        ) : (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cn(
              'h-8 rounded-full px-3 text-sm font-medium',
              tab === t ? 'bg-ink text-bg' : 'bg-surface-2 text-ink-2',
            )}
          >
            {t === 'todo' ? 'To Do' : 'Done'}
          </button>
        ),
      )}
      {tab === 'done' && (
        <span className="ml-auto flex items-center gap-1 text-cap text-ink-3">
          <ArrowDown className="size-3.5" />
          Date done
        </span>
      )}
    </div>
  );

  if (!desktop) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title="My Tasks" />
        {tabs}
        {list}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="My Tasks" />
      <div className="flex min-h-0 flex-1 flex-col gap-3 bg-surface-1 px-5 pt-4 pb-4">
        <div className="flex flex-wrap items-baseline gap-x-3">
          <h1 className="text-xl font-semibold">
            {greeting(localHour)}, {firstName}
          </h1>
          <span className="text-sm text-ink-3">
            {dueToday} due today{overdue ? ` · ${overdue} overdue` : ''}
          </span>
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_300px] gap-3 max-lg:grid-cols-1">
          <section
            className="flex min-h-0 flex-col overflow-hidden rounded-md border border-line bg-raised shadow-card"
            aria-label="My Work"
          >
            <div className="flex items-center px-4 pt-3">
              <h2 className="text-md font-semibold">My Work</h2>
            </div>
            {tabs}
            <div className="flex min-h-0 flex-1 flex-col px-2 pt-1">{list}</div>
          </section>
          <aside className="flex min-h-0 flex-col gap-4 max-lg:hidden">
            <UpcomingRemindersCard tz={tz} />
            <RecentsCard />
          </aside>
        </div>
      </div>
    </div>
  );
}

function GroupHeader({
  label,
  count,
  tone,
  collapsed,
  onToggle,
  phone,
  sort,
  onSort,
  onAddTask,
  onAddReminder,
}: {
  label: string;
  count: number;
  tone?: 'overdue';
  collapsed: boolean;
  onToggle: () => void;
  phone: boolean;
  sort?: GroupSort;
  onSort: (s: GroupSort) => void;
  onAddTask: () => void;
  onAddReminder: () => void;
}) {
  return (
    <div
      className={cn(
        'group/h flex items-center gap-2 px-2',
        phone ? 'h-10 bg-surface-1 px-4' : 'h-8',
      )}
    >
      <button
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex items-center gap-2 rounded-sm py-1 pr-1 font-medium"
      >
        {collapsed ? (
          <ChevronRight className="size-3.5 text-ink-2" />
        ) : (
          <ChevronDown className="size-3.5 text-ink-2" />
        )}
        {label}
      </button>
      <span className={cn('tabular text-cap', tone === 'overdue' ? 'text-overdue' : 'text-ink-3')}>
        {count}
      </span>
      <Menu>
        <MenuTrigger asChild>
          <IconButton
            label={`Add to ${label}`}
            size="sm"
            className={cn(
              !phone &&
                'opacity-0 group-hover/h:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100',
            )}
          >
            <Plus className="size-3.5" />
          </IconButton>
        </MenuTrigger>
        <MenuContent className="w-44">
          <MenuItem icon={<Check className="size-4" />} onSelect={onAddTask}>
            Add Task
          </MenuItem>
          <MenuItem icon={<BellRing className="size-4" />} onSelect={onAddReminder}>
            Add Reminder
          </MenuItem>
        </MenuContent>
      </Menu>
      <span className="flex-1" />
      <Menu>
        <MenuTrigger asChild>
          <button
            className={cn(
              'flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-cap text-ink-3 hover:bg-surface-2',
              !sort &&
                !phone &&
                'opacity-0 group-hover/h:opacity-100 data-[state=open]:opacity-100',
            )}
          >
            {sort?.key === 'manual' ? null : sort?.direction === 'desc' ? (
              <ArrowDown className="size-3.5" />
            ) : (
              <ArrowUp className="size-3.5" />
            )}
            {SORT_LABEL[sort?.key ?? 'due_date']}
          </button>
        </MenuTrigger>
        <MenuContent align="end" className="w-52">
          <MenuLabel>Sort this group by</MenuLabel>
          {(['status', 'due_date', 'created', 'updated', 'priority'] as TaskSortKey[]).map((k) => (
            <MenuItem
              key={k}
              onSelect={() =>
                onSort({
                  key: k,
                  direction: sort?.key === k && sort.direction === 'asc' ? 'desc' : 'asc',
                })
              }
              shortcut={
                sort?.key === k || (!sort && k === 'due_date')
                  ? (sort?.direction ?? 'asc') === 'asc'
                    ? '↑'
                    : '↓'
                  : undefined
              }
            >
              {SORT_LABEL[k]}
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuItem
            onSelect={() => onSort({ key: 'manual', direction: 'asc' })}
            shortcut={sort?.key === 'manual' ? '✓' : 'Drag & drop'}
          >
            Custom
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  );
}

function UpcomingRemindersCard({ tz }: { tz: string }) {
  const reminders = useUpcomingReminders(5);
  const { ws } = useTaskRowData();
  const tasks = useAllTasks();
  const byId = useMemo(() => new Map((tasks ?? []).map((t) => [t.id, t])), [tasks]);
  const openTask = useOpenTask();
  return (
    <section
      className="rounded-md border border-line bg-raised p-4 shadow-card"
      aria-label="Upcoming reminders"
    >
      <h2 className="mb-2 text-md font-semibold">Upcoming reminders</h2>
      {!reminders?.length && (
        <p className="text-sm text-ink-3">
          No reminders scheduled. Tasks with a due date get reminders automatically.
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {reminders?.map((r) => {
          const task = r.task_id ? byId.get(r.task_id) : undefined;
          return (
            <li key={r.id} className="flex items-start gap-2">
              <BellRing className="mt-0.5 size-3.5 shrink-0 text-ink-2" />
              <button
                className="min-w-0 flex-1 text-left"
                onClick={() => task && openTask(task.id)}
              >
                <div className="truncate">{task?.name ?? r.title ?? 'Reminder'}</div>
                <div className="text-cap text-ink-3">
                  {formatFireAt(r.snoozed_until ?? r.fire_at, tz)}
                  {r.state === 'snoozed' ? ' · snoozed' : ''}
                  {task ? ` · ${ws.listById.get(task.list_id)?.name ?? ''}` : ''}
                </div>
              </button>
              <Menu>
                <MenuTrigger asChild>
                  <button className="rounded-sm border border-line px-2 py-0.5 text-cap hover:bg-surface-2">
                    Snooze
                  </button>
                </MenuTrigger>
                <MenuContent align="end">
                  <MenuItem onSelect={() => void m.snoozeReminder(r.id, '10m')}>
                    10 minutes
                  </MenuItem>
                  <MenuItem onSelect={() => void m.snoozeReminder(r.id, '1h')}>1 hour</MenuItem>
                  <MenuItem onSelect={() => void m.snoozeReminder(r.id, 'tomorrow')}>
                    Tomorrow morning
                  </MenuItem>
                </MenuContent>
              </Menu>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function RecentsCard() {
  const recents = usePrefs((s) => s.recents);
  const tasks = useAllTasks();
  const { ws, resolve } = useTaskRowData();
  const openTask = useOpenTask();
  const byId = useMemo(() => new Map((tasks ?? []).map((t) => [t.id, t])), [tasks]);
  const rows = recents
    .map((r) =>
      r.kind === 'task' ? { r, task: byId.get(r.id) } : { r, list: ws.listById.get(r.id) },
    )
    .filter((x) => ('task' in x ? x.task && !x.task.deleted_at : x.list))
    .slice(0, 10);
  return (
    <section
      className="min-h-0 flex-1 overflow-y-auto rounded-md border border-line bg-raised p-4 shadow-card scroll-thin"
      aria-label="Recents"
    >
      <h2 className="mb-2 text-md font-semibold">Recents</h2>
      {rows.length === 0 && (
        <p className="text-sm text-ink-3">Tasks and lists you open show up here.</p>
      )}
      <ul className="flex flex-col">
        {rows.map((x) => {
          if ('task' in x && x.task) {
            const d = resolve(x.task);
            return (
              <li key={x.r.id}>
                <button
                  onClick={() => openTask(x.task!.id)}
                  className="flex h-8 w-full items-center gap-2 rounded-sm px-1 text-left hover:bg-surface-2"
                >
                  <StatusIcon status={d.status} />
                  <span className="min-w-0 truncate">{x.task.name}</span>
                  <span className="shrink-0 text-sm text-ink-3">
                    · in {d.list?.emoji} {d.list?.name}
                  </span>
                </button>
              </li>
            );
          }
          const l = 'list' in x ? x.list : undefined;
          if (!l) return null;
          return (
            <li key={x.r.id}>
              <a
                href={`/l/${l.id}`}
                className="flex h-8 items-center gap-2 rounded-sm px-1 hover:bg-surface-2"
              >
                <span>{l.emoji ?? '📋'}</span>
                <span className="truncate">{l.name}</span>
                <span className="text-sm text-ink-3">
                  · in {ws.spaceById.get(l.space_id)?.name}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
